import { Icon, IconID } from '@hub/common/Icon/Icon';
import classNames from 'classnames';
import type { ReactElement } from 'react';
import { useMemo } from 'react';

type TablePaginationButtonProps = {
  targetPage: number;
  page: number;
  changeActivePage: (page: number) => void;
};

const TablePaginationButton = ({ targetPage, page, changeActivePage }: TablePaginationButtonProps) => (
  <button
    type="button"
    aria-label={`go to page ${targetPage}`}
    className={classNames(
      'px-2 py-1 mx-1 w-12 rounded-md',
      targetPage === page && 'bg-igus-primary text-white font-bold'
    )}
    onClick={() => changeActivePage(targetPage)}
  >
    {targetPage}
  </button>
);

const Separator = () => <span className="px-2">&hellip;</span>;

export interface PaginatorProps {
  page: number;
  total: number;
  onChangePage: (page: number) => void;
}

export const TablePaginator = ({ page, total, onChangePage }: PaginatorProps) => {
  const buttons = useMemo(() => {
    const btns: ReactElement[] = [];

    if (total <= 6) {
      //  < 1 2 3 4 5 >
      for (let i = 1; i <= total; i += 1) {
        btns.push(<TablePaginationButton key={i} targetPage={i} page={page} changeActivePage={onChangePage} />);
      }
    } else if (page <= 5) {
      //  < 1 2 3 4 5 … 22 >
      for (let i = 1; i <= 5; i += 1) {
        btns.push(<TablePaginationButton key={i} targetPage={i} page={page} changeActivePage={onChangePage} />);
      }
      btns.push(<Separator key="sep1" />);
      btns.push(<TablePaginationButton key={total} targetPage={total} page={page} changeActivePage={onChangePage} />);
    } else if (page > 5 && page < total - 4) {
      // < 1 … 14 15 16 … 22 >
      btns.push(<TablePaginationButton key={1} targetPage={1} page={page} changeActivePage={onChangePage} />);
      btns.push(<Separator key="sep1" />);
      btns.push(
        <TablePaginationButton key={page - 1} targetPage={page - 1} page={page} changeActivePage={onChangePage} />
      );
      btns.push(<TablePaginationButton key={page} targetPage={page} page={page} changeActivePage={onChangePage} />);
      btns.push(
        <TablePaginationButton key={page + 1} targetPage={page + 1} page={page} changeActivePage={onChangePage} />
      );
      btns.push(<Separator key="sep2" />);
      btns.push(<TablePaginationButton key={total} targetPage={total} page={page} changeActivePage={onChangePage} />);
    } else if (page >= total - 4) {
      //  < 1 … 18 19 20 21 22 >
      btns.push(<TablePaginationButton key={1} targetPage={1} page={page} changeActivePage={onChangePage} />);
      btns.push(<Separator key="sep1" />);
      for (let i = total - 4; i <= total; i += 1) {
        btns.push(<TablePaginationButton key={i} targetPage={i} page={page} changeActivePage={onChangePage} />);
      }
    }
    return btns;
  }, [page, total]);

  return (
    <div className="self-center flex flex-row pl-6">
      <div
        aria-label="previous page"
        className="pb-1 cursor-pointer"
        onClick={() => onChangePage(page !== 1 ? page - 1 : 1)}
      >
        <Icon id={IconID.ARROW_PAGINATION_LEFT} size={8} />
      </div>

      <div data-testid="paginator-indicator" className={`self-center text-center ${total > 6 ? 'w-96' : 'px-4'}`}>
        {buttons}
      </div>

      <div
        aria-label="next page"
        className="pb-1 cursor-pointer"
        onClick={() => onChangePage(page !== total ? page + 1 : total)}
      >
        <Icon id={IconID.ARROW_PAGINATION_RIGHT} size={8} />
      </div>
    </div>
  );
};
